/**
 * Componente ICalImportButton
 * Botón para importar calendario desde archivo iCal
 * @module components/appointments/ICalImportButton
 */

import React, { useState, useRef } from 'react';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useICalImport } from '@/hooks/useICalExport';
import type { ICalImportParams } from '@/types/calendar.types';
import { Button } from '@/components/ui/button';

interface ICalImportButtonProps {
  onSuccess?: () => void;
  params?: ICalImportParams;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}

export const ICalImportButton: React.FC<ICalImportButtonProps> = ({
  onSuccess,
  params,
  variant = 'default',
  size = 'default',
  className = '',
}) => {
  const { mutateAsync: importCalendar, isPending } = useICalImport();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setError(null);
      setSuccess(null);
      
      const result = await importCalendar({ file, params });

      // Mostrar resultado
      setSuccess(
        `Importación completada: ${result.imported} importadas, ${result.skipped} omitidas`
      );

      onSuccess?.();
    } catch (err) {
      console.error('Error al importar calendario:', err);
      setError('Error al importar calendario. Verifica el archivo e intenta nuevamente.');
    } finally {
      // Limpiar input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".ics"
        onChange={handleFileChange}
        className="hidden"
      />
      <Button
        onClick={() => fileInputRef.current?.click()}
        disabled={isPending}
        variant={variant}
        size={size}
        className="inline-flex items-center gap-2"
      >
        <ArrowUpTrayIcon className="w-5 h-5" />
        {isPending ? 'Importando...' : 'Importar desde iCal'}
      </Button>
      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
      {success && <p className="text-sm text-green-600 mt-1">{success}</p>}
    </div>
  );
};
